import React, { useState, useEffect, useCallback, useRef } from "react";
import { ApiService } from "../../service/api.service";
import { AUTH_URL } from "../../common/common";
import { useNavigate } from "react-router-dom";
import { EmployeeModalDropDownProps } from "../../service/interfaces/props.interface";

const EmployeeModalDropDown: React.FC<EmployeeModalDropDownProps> = ({
  label,
  name,
  endpoint,
  register,
  errors,
  disabled = false,
  defaultValue = "",
  setValue,
  fetchDepartments,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState<
    { label: string; value: string | number; department: string; email: string; phone: string; position: string }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<{ label: string; value: string | number } | null>(null);
  const [initialFetchDone, setInitialFetchDone] = useState(false);

  // Flag to track if default value has been processed
  const defaultValueProcessedRef = useRef(false);
  // Flag to track if fetchDepartments has been called for this default value
  const departmentsFetchedRef = useRef(false);

  const apiService = new ApiService();
  const entityId = localStorage.getItem("entity_id");
  const navigate = useNavigate();

  // Use a ref to track the current request so we can cancel it
  const currentRequestRef = useRef<AbortController | null>(null);

  // Add overflow hidden to body when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Only fetch options when dropdown is opened and not fetched before
  useEffect(() => {
    if (open && !initialFetchDone) {
      fetchOptions();
      setInitialFetchDone(true);
    }
  }, [open, initialFetchDone]);

  // Debounce function to reduce API calls
  const debounce = (func: Function, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  };

  const debouncedSearch = useCallback(
    debounce((searchTerm: string) => {
      if (open) {
        fetchOptions(searchTerm);
      }
    }, 500),
    [endpoint, entityId, open]
  );

  // Handle search input
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    debouncedSearch(value);
  };

  // Fetch options from API with proper cancellation
  const fetchOptions = async (searchTerm: string = "") => {
    // Cancel any ongoing request
    if (currentRequestRef.current) {
      currentRequestRef.current.abort();
    }

    // Create a new abort controller for this request
    const abortController = new AbortController();
    currentRequestRef.current = abortController;

    try {
      setLoading(true);
      setError(null);

      const response: any = await apiService.getApi(
        `${AUTH_URL}/${endpoint}?status=${1}&entityId=${entityId || ""}&search=${searchTerm}`,
        abortController.signal
      );

      // Only process the response if this request wasn't canceled
      if (!abortController.signal.aborted) {
        if (response && response.body && Array.isArray(response.body.data)) {
          const formattedOptions = response.body.data.map((item: any) => ({
            label: `${item.firstname || ""} ${item.lastname || ""}`.trim() || "Select Employee",
            value: item.employeeid || item.id,
            email: item.emailid || "N/A",
            phone: item.mobilenumber || "N/A",
          }));
          setOptions(formattedOptions);
        } else {
          setOptions([]);
        }
      }
    } catch (err: any) {
      // Only set error if this request wasn't canceled
      if (err.name !== "CanceledError" && err.name !== "AbortError") {
        console.error("API fetch error:", err);
        setError("Failed to fetch employees");
        setOptions([]);
      }
    } finally {
      if (currentRequestRef.current === abortController) {
        setLoading(false);
        currentRequestRef.current = null;
      }
    }
  };

  // Fetch employee details by ID using the endpoint
  const fetchOptionById = async (value: string | number) => {
    if (!value) return;

    try {
      setLoading(true);

      // First try the employee/details-route endpoint 
      const detailsEndpoint = `${AUTH_URL}/employee/get-one-route?status=1&employeeId=${value}`;
      let response: any = await apiService.getApi(detailsEndpoint);

      // If data found from details-route endpoint
      if (response && response.body) {
        // Extract employee details from the nested structure
        const employeeData = response.body;

        if (employeeData) {
          const selectedOption = {
            label: `${employeeData.firstname || ""} ${employeeData.lastname || ""}`.trim() || "Select Employee",
            value: employeeData.employeeId || employeeData.id,
            email: employeeData.emailId || "N/A",
            phone: employeeData.phoneNumber || "N/A",
          };

          setSelected(selectedOption);

          // Only fetch departments if it hasn't been done yet for this default value
          if (fetchDepartments && !departmentsFetchedRef.current) {
            departmentsFetchedRef.current = true;
            fetchDepartments(employeeData?.employeeId || employeeData?.id);
          }
          return;
        }
      }

      // If details-route didn't work, fall back to the provided endpoint
      response = await apiService.getApi(
        `${AUTH_URL}/${endpoint}?status=1&entityId=${entityId || ""}&id=${value}`
      );

      if (response && response.body && Array.isArray(response.body.data) && response.body.data.length > 0) {
        const item = response.body.data[0];
        const selectedOption = {
          label: `${item.firstName || ""} ${item.lastName || ""}`.trim() || "Select Employee",
          value: item.employeeId || item.id,
          email: item.emailId || "N/A",
          department: item.department || "N/A",
          phone: item.phoneNumber || "N/A",
          position: item.position || "N/A",
        };

        setSelected(selectedOption);

        // Only fetch departments if it hasn't been done yet for this default value
        if (fetchDepartments && !departmentsFetchedRef.current) {
          departmentsFetchedRef.current = true;
          fetchDepartments(item?.employeeId || item?.id);
        }
      }
    } catch (err) {
      console.error("Failed to fetch selected employee data:", err);
    } finally {
      setLoading(false);
    }
  };

  // Set default value when component initializes or when it changes
  useEffect(() => {
    if (defaultValue && !defaultValueProcessedRef.current) {
      defaultValueProcessedRef.current = true;
      fetchOptionById(defaultValue);
    } else if (defaultValue && selected && defaultValue !== selected.value) {
      // Reset if default value changes
      defaultValueProcessedRef.current = false;
      departmentsFetchedRef.current = false;
      setSelected(null);
      fetchOptionById(defaultValue);
    }
  }, [defaultValue]);

  // Clean up any pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (currentRequestRef.current) {
        currentRequestRef.current.abort();
      }
    };
  }, []);

  // Handle selection and send value to form
  const handleSelect = (option: {
    label: string;
    value: string | number;
    department: string;
    email: string;
    phone: string;
    position: string;
  }) => {
    setSelected(option);
    setOpen(false);
    if (setValue) {
      setValue(name, option.value, { shouldValidate: true, shouldDirty: true });
    }

    // Only call fetchDepartments if it's a new selection (not from default value)
    // and only if the value is different from the current default value
    if (fetchDepartments && option.value && option.value !== defaultValue) {
      departmentsFetchedRef.current = true; // Mark as fetched to prevent duplicate calls
      fetchDepartments(option.value);
    }
  };

  // Filter options by search term
  const filteredOptions = options
    .filter((option) =>
      option.label.toLowerCase().includes(search.toLowerCase())
    )
    .map((option) => ({
      ...option,
      email: option.email || "N/A",
      phone: option.phone || "N/A",
    }));

  // Register form input
  const { ref, ...rest } = register(name);

  // Close modal when clicking outside or pressing ESC
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  return (
    <div className="w-full">
      <label htmlFor={name} className="input-label">
        {label ? label : <>&nbsp;</>}
      </label>

      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setOpen(true)}
          className={`w-full px-4 py-2 border rounded-lg font-medium bg-white cursor-pointer border-gray-300 text-[14px]
            focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all
            ${disabled ? "opacity-50 cursor-not-allowed" : ""}
            ${errors[name] ? "border-red-500" : ""}
          `}
        >
          {selected ? selected.label : <span className="text-gray-400">{`Select ${label || "employee"}`}</span>}
        </button>

        {/* Hidden input to hold the form value */}
        <input
          {...rest}
          ref={ref}
          type="hidden"
          value={selected?.value || ""}
          id={name}
          name={name}
        />

        {errors[name] && <p className="input-error-message">{errors[name]?.message}</p>}
      </div>

      {/* Modal with full screen overlay */}
      {open && (
        <>
          {/* Fixed position overlay that covers the entire viewport with higher z-index */}
          <div
            className="fixed inset-0 bg-black bg-opacity-75 z-[10001]"
            onClick={() => setOpen(false)}
          ></div>

          {/* Modal content with higher z-index than the sidebar */}
          <div
            className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl w-[95%] max-w-4xl p-4 md:p-6 animate-fadeIn z-[10002]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold">Select {label || "employee"}</h2>
              <div className="gap-2 md:gap-4 flex items-center">
                <button
                  type="button"
                  onClick={() => navigate('/employee/add')}
                  className="text-sm md:text-base text-[#444FBD] font-[600] hover:underline"
                >
                  Add new employee
                </button>
                <button
                  onClick={() => setOpen(false)}
                  className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
                  aria-label="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>
            </div>

            {/* Search Bar */}
            <input
              type="text"
              placeholder="Search employees..."
              value={search}
              onChange={handleSearchChange}
              className="w-full px-4 py-2 border rounded-md mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />

            {/* Loading and Error States */}
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : error ? (
              <div className="text-center text-red-500 py-4">
                {error}
                <button onClick={() => fetchOptions(search)} className="text-blue-500 underline ml-2">
                  Retry
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <div className="overflow-y-auto max-h-[50vh]">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 text-gray-700 sticky top-0">
                      <tr>
                        <th className="px-2 md:px-4 py-2 text-left">Name</th>
                        <th className="px-2 md:px-4 py-2 text-left hidden md:table-cell">Email</th>
                        <th className="px-2 md:px-4 py-2 text-center">Select</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOptions.length > 0 ? (
                        filteredOptions.map((option) => (
                          <tr
                            key={option.value.toString()}
                            className="hover:bg-gray-50 transition cursor-pointer border-b border-gray-100"
                            onClick={() => handleSelect(option)}
                          >
                            <td className="px-2 md:px-4 py-2">{option.label}</td>
                            <td className="px-2 md:px-4 py-2 hidden md:table-cell">{option.email}</td>
                            <td className="px-2 md:px-4 py-2 text-center">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelect(option);
                                }}
                                className="bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600 transition text-xs md:text-sm"
                              >
                                Select
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5} className="px-4 py-4 text-center text-gray-500">
                            No employees found. Try adjusting your search.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default EmployeeModalDropDown;